/**
 * My Library Service - API Functions
 * Handles image library operations for both guest and authenticated users
 */

import { get, post, del, patch } from '@/services/shared/client'
import type { StoredImage } from '@/types/image'
import { normalizeImages } from '@/types/image'
import { MAX_FILE_SIZE } from '@/helpers/images'
import { startDirectUploadBatch } from '@/services/library/uploadClient'
import { getDirectUploadHostConcurrency } from '@/utils/env'

// ==================== Response Types ====================

/**
 * Response for GET /library/guest/images and GET /library/user/images
 */
export interface LibraryResponse {
  success: boolean
  message?: string
  data: {
    images: StoredImage[]
    total: number
    limit: number
    offset: number
    hasMore: boolean
  }
}

export interface UploadFailure {
  filename: string
  error: string
}

export interface NormalizedUploadResponse {
  success: boolean
  message?: string
  images: StoredImage[]
  failed?: UploadFailure[]
}

/**
 * Response for DELETE endpoints
 */
export interface DeleteResponse {
  success: boolean
  message?: string
  data: {
    deleted: number
    failed?: number
  }
}

/**
 * Response for PATCH /library/user/favorite
 */
export interface FavoriteResponse {
  success: boolean
  message?: string
  data: {
    updated: number
    failed?: number
  }
}

/**
 * Response for POST /library/transfer
 */
export interface TransferResponse {
  success: boolean
  message?: string
  data: {
    transferred: number
    moved?: number
    failed?: number
    /** Map of old URLs to new URLs for images that were moved to user folder */
    urlMap?: Record<string, string>
  }
}

// ==================== Helper Functions ====================

// ==================== Guest (Unauthenticated) Routes ====================

/**
 * Get images for a guest invitation
 */
export async function getGuestImages(
  invitationId: string,
  options?: { limit?: number; offset?: number }
): Promise<LibraryResponse> {
  const params = new URLSearchParams({ invitationId })
  if (options?.limit) params.append('limit', options.limit.toString())
  if (options?.offset) params.append('offset', options.offset.toString())

  const response = await get<LibraryResponse>(`/library/guest/images?${params}`)

  // Normalize images to add computed fields
  return {
    ...response,
    data: {
      ...response.data,
      images: normalizeImages(response.data.images),
    },
  }
}

/**
 * Upload image(s) as a guest
 * invitationId is REQUIRED - all uploads must have invitation context
 * Returns normalized response with images array
 */
export async function uploadGuestImages(
  files: File | File[],
  invitationId: string,
  type: 'og' | 'edit' = 'og'
): Promise<NormalizedUploadResponse> {
  if (!invitationId) {
    throw new Error('invitationId is required for guest uploads')
  }

  const fileArray = Array.isArray(files) ? files : [files]
  return uploadImagesDirect({
    files: fileArray,
    invitationId,
    mode: 'guest',
    type,
  })
}

/**
 * Cleanup ALL images for a guest invitation (abandoned session)
 * Used when user navigates away without signing in
 */
export async function cleanupGuestImages(
  invitationId: string
): Promise<DeleteResponse> {
  if (!invitationId) {
    throw new Error('Invitation ID is required')
  }

  return del<DeleteResponse>(
    `/library/guest/cleanup?invitationId=${encodeURIComponent(invitationId)}`
  )
}

/**
 * Delete specific guest images by ID
 */
export async function deleteGuestImages(
  invitationId: string,
  imageIds: string[]
): Promise<DeleteResponse> {
  if (!invitationId) {
    throw new Error('Invitation ID is required')
  }
  if (!imageIds || imageIds.length === 0) {
    throw new Error('At least one image ID is required')
  }

  return del<DeleteResponse>('/library/guest/delete', {
    invitationId,
    imageIds,
  })
}

// ==================== User (Authenticated) Routes ====================

/**
 * Get user images (flat library - images belong to user, not invitation)
 * @param options.favorites - If true, only return favorited images
 */
export async function getUserImages(options?: {
  limit?: number
  offset?: number
  favorites?: boolean
}): Promise<LibraryResponse> {
  const params = new URLSearchParams()
  if (options?.limit) params.append('limit', options.limit.toString())
  if (options?.offset) params.append('offset', options.offset.toString())
  if (options?.favorites) params.append('favorites', 'true')

  const query = params.toString()
  const response = await get<LibraryResponse>(
    `/library/user/images${query ? `?${query}` : ''}`
  )

  // Normalize images to add computed fields
  return {
    ...response,
    data: {
      ...response.data,
      images: normalizeImages(response.data.images),
    },
  }
}

/**
 * Upload image(s) as authenticated user
 * Images belong to user (flat library), not to any specific invitation
 * Returns normalized response with images array
 */
export async function uploadUserImages(
  files: File | File[],
  type: 'og' | 'edit' = 'og'
): Promise<NormalizedUploadResponse> {
  const fileArray = Array.isArray(files) ? files : [files]
  return uploadImagesDirect({
    files: fileArray,
    mode: 'user',
    type,
  })
}

interface UploadImagesByModeParams {
  files: File[]
  invitationId?: string
  mode: 'guest' | 'user'
  type: 'og' | 'edit'
}

function resolveDirectUploadConcurrency(): number {
  if (typeof getDirectUploadHostConcurrency !== 'function') return 1
  return getDirectUploadHostConcurrency()
}

async function uploadImagesDirect({
  files,
  invitationId,
  mode,
  type,
}: UploadImagesByModeParams): Promise<NormalizedUploadResponse> {
  if (!files.length) {
    return { success: true, images: [] }
  }

  const task = startDirectUploadBatch({
    files,
    invitationId,
    authState: mode === 'guest' ? 'unauth' : 'auth',
    expectedImageType: type,
    maxBytes: MAX_FILE_SIZE,
    concurrency: resolveDirectUploadConcurrency(),
  })

  const result = await task.promise
  return {
    success: true,
    images: normalizeImages(result.images),
    ...(result.failed.length > 0 ? { failed: result.failed } : {}),
  }
}

/**
 * Delete specific user images by ID
 */
export async function deleteUserImages(
  imageIds: string[]
): Promise<DeleteResponse> {
  if (!imageIds || imageIds.length === 0) {
    throw new Error('At least one image ID is required')
  }

  return del<DeleteResponse>('/library/user/delete', { imageIds })
}

/**
 * Get guest uploads from user's invitations
 * These are photos uploaded by guests to the user's invitations
 * @param options.invitationId - Filter to specific invitation (optional)
 * @param options.limit - Max items per page (default 50)
 * @param options.offset - Pagination offset (default 0)
 */
export async function getUserGuestUploads(options?: {
  invitationId?: string
  limit?: number
  offset?: number
}): Promise<LibraryResponse> {
  const params = new URLSearchParams()
  if (options?.invitationId) params.append('invitationId', options.invitationId)
  if (options?.limit) params.append('limit', options.limit.toString())
  if (options?.offset) params.append('offset', options.offset.toString())

  const query = params.toString()
  const response = await get<LibraryResponse>(
    `/library/user/guest-uploads${query ? `?${query}` : ''}`
  )

  return {
    ...response,
    data: {
      ...response.data,
      images: normalizeImages(response.data.images),
    },
  }
}

/**
 * Toggle favorite status for user images
 * @param imageIds - Array of image IDs to update
 * @param favorite - Whether to mark as favorite (true) or unfavorite (false)
 */
export async function toggleFavoriteImages(
  imageIds: string[],
  favorite: boolean
): Promise<FavoriteResponse> {
  if (!imageIds || imageIds.length === 0) {
    throw new Error('At least one image ID is required')
  }

  return patch<FavoriteResponse>('/library/user/favorite', {
    imageIds,
    favorite,
  })
}

// ==================== Transfer Route ====================

/**
 * Transfer guest images to user account after sign-in
 */
export async function transferGuestImages(
  invitationId: string
): Promise<TransferResponse> {
  if (!invitationId) {
    throw new Error('Invitation ID is required')
  }

  return post<TransferResponse>('/library/transfer', { invitationId })
}
